import React, { useContext, useEffect, useState } from 'react';
import {
  Grid,
  InputAdornment,
  styled,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { useIntl } from 'react-intl';
import translations from 'utils/translations';
import PageHeader from 'pages/breakout/components/PageHeader';
import {
  SdmSpacer,
  SdmTextButton,
  SdmTextField,
  SdmTypography,
} from 'components';
import { theme } from 'theme';
import { LoaderContext } from 'context/LoaderContextProvider';
import { useHistory } from 'react-router-dom';
import { VisitsContext } from 'context/VisitsContextProvider';
import {
  createSlot,
  deleteBookingRequest,
  deleteSlot,
  getBrand,
  updateBookingRequest,
} from 'entities/agenda/agendaHelper';
import AppointmentCard from './Agenda/AppointmentCard';
import DeleteDialog from './Agenda/Dialogs/DeleteDialog';
import ViewDialog from './Agenda/Dialogs/ViewDialog';
import AddDialog from './Agenda/Dialogs/AddDialog';
import { SaloneStartDate, slotContext, slotStatus } from 'utils/constants';
import RequestCard from './Agenda/RequestCard';
import { useEmailHelper } from 'services/emailSender';
import { format, isAfter } from 'date-fns';
import {
  getExhibitorSettings,
  hasValidSettings,
  updateExhibitorSettings,
} from './common/utils';
import { useNetwork } from 'services/network';
import SettingsGreyBox from './Agenda/SettingsGreyBox';
import SettingsDialog from './Agenda/Dialogs/SettingsDialog';

const breakpoints = {
  values: {
    xs: 0,
    sm: 600,
    md: 768,
    lg: 992,
    xl: 1200,
    xxl: 1440,
  },
};

export function responsiveFontSizesLineHeight({
  md,
  mdLineHeight,
  lg,
  lgLineHeight,
}) {
  return {
    [`@media (min-width:${breakpoints.values.sm}px)`]: {
      fontSize: md,
      lineHeight: mdLineHeight,
    },
    [`@media (min-width:${breakpoints.values.lg}px)`]: {
      fontSize: lg,
      lineHeight: lgLineHeight,
    },
  };
}

const CardContainer = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  width: '90%',
  margin: '0 auto',
  padding: '0px 15px',
  paddingBottom: 64,
  [`@media (min-width: 768px)`]: {
    margin: '0 auto',
    width: '750px',
  },
  [`@media (min-width: 992px)`]: {
    width: '970px',
  },
  [`@media (min-width: 1280px)`]: {
    width: '1280px',
  },
});

const CustomSdmTextField = styled(SdmTextField)(({ ismobile }) => ({
  backgroundColor: 'transparent',
  borderBottom: `1px solid black`,
  borderRadius: 0,
  paddingBottom: 13,
  width: '140%',
  marginBottom: 20,
  '& ::-webkit-input-placeholder': {
    /* Chrome/Opera/Safari */ color: 'black',
    opacity: 1,
    fontFamily: 'Suisse-Intl',
    fontSize: ismobile ? '1rem' : 24,
  },
  '& .MuiInput-input': {
    input: {
      '&::placeholder': {
        color: 'black',
      },
    },
    color: theme.colors.primary.black,
    fontSize: ismobile ? '1rem' : '1.250rem',
    fontFamily: 'Suisse-Intl',
    ...(ismobile && {
      marginBottom: '0px !important',
    }),
  },
}));

const StyledTab = styled(SdmTypography)(({ isSelected }) => ({
  width: '50%',
  padding: '15px 0px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderBottom: isSelected ? `2px solid #E2001A` : `2px solid #E1E1E1`,
  lineHeight: '24px',
  letterSpacing: 0,
  margin: 0,
  fontSize: '16px',
  fontFamily: 'Suisse-Intl',
  ...responsiveFontSizesLineHeight({
    md: '16px',
    mdLineHeight: '24px',
    lg: '20px',
    lgLineHeight: '28px',
  }),
  '& div': {
    margin: 0,
  },
}));

const CustomStack = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'row',
  width: '100%',
  maxWidth: '1280px',
  margin: '0 auto',
}));

const Agenda = ({ locale, loggedUser }) => {
  // Hooks
  const intl = useIntl();
  const i18n = translations.Agenda(intl).agenda.page;
  const history = useHistory();
  const mediaTheme = useTheme();
  const network = useNetwork();
  const isSmDown = useMediaQuery(mediaTheme.breakpoints.down('sm'));
  const isXsDown = useMediaQuery(mediaTheme.breakpoints.down('xs'));
  const emailHelper = useEmailHelper();
  const { setLoaderActive, isLoaderActive } = useContext(LoaderContext);
  const { setBrandId, setSelectedSlot, setSalesTeam, setExhibitorAgenda } =
    useContext(VisitsContext);
  //Loader Function
  const showLoader = () => setLoaderActive(true);
  const hideLoader = () => setLoaderActive(false);
  // States
  const [brand, setBrand] = useState();
  const [brandSalesforceId, setBrandSalesforceId] = useState();
  const [brandSettings, setBrandSettings] = useState();
  const [searchText, setSearchText] = useState('');
  const [deleteDialog, setDeleteDialog] = useState({
    isOpen: false,
    id: '',
    isApp: false,
    customText: '',
    customSubText: '',
    app: {},
    slot: {},
    multipleSlotsIds: [],
  });
  const [viewDialog, setViewDialog] = useState({
    isOpen: false,
    slot: {},
  });
  const [addDialog, setAddDialog] = useState({
    isOpen: false,
    dayTime: '',
    appDate: '',
    appointmentId: '',
  });
  const [settingsDialog, setSettingsDialog] = useState({
    isOpen: false,
    email: '',
    prenotazione_appuntamenti: '0',
  });
  const [contextSelected, setContextSelected] = useState(slotContext.REQUEST);
  // Effects
  useEffect(() => {
    if (!brand) fetchSalesMember();
  }, [brand]);

  useEffect(() => {
    if (brandSalesforceId) {
      const fetchBrandSettings = async () => {
        const res = await getExhibitorSettings({
          network,
          id: brandSalesforceId,
        });
        if (hasValidSettings(res)) {
          setBrandSettings({
            email: res.email || brand.email || '',
            prenotazione_appuntamenti:
              res.prenotazione_appuntamenti === false ? '1' : '0',
          });
        }
        hideLoader();
      };
      fetchBrandSettings();
    }
  }, [brandSalesforceId]);

  // Functions
  const createVisit = () => history.push('/create');

  const fetchSalesMember = async () => {
    showLoader();
    const params = new URLSearchParams(location.search);
    const id = params.get('salesforce_id');

    if (id) {
      const _brand = await getBrand({ id });
      setBrand(_brand);
      setExhibitorAgenda(_brand);
      setBrandId(_brand.id);
      setSalesTeam(_brand.salesTeam.items);
      setBrandSalesforceId(id);
      return;
    }
    if (loggedUser.salesforceIdBrand) {
      const _brand = await getBrand({ id: loggedUser.salesforceIdBrand });
      setBrand(_brand);
      setExhibitorAgenda(_brand);
      setBrandId(_brand.id);
      setSalesTeam(_brand.salesTeam.items);
      setBrandSalesforceId(id);
      return;
    }
    hideLoader();
  };

  const handleViewSlot = (slot) => {
    setViewDialog({ isOpen: true, slot });
  };

  const handleViewRequest = (req) => {
    setSelectedSlot(req);
    history.push('/request-page');
  };

  const handleDeleteRequest = (req) => {
    setDeleteDialog({
      isOpen: true,
      id: req.id,
      isApp: true,
      customText: i18n.dialogs.delete.titleAppointment,
      customSubText: i18n.dialogs.delete.subTextSlot,
      app: req,
      slot: {},
      multipleSlotsIds: [],
    });
  };

  const handleCheckRequest = async (req) => {
    showLoader();
    try {
      const id = req.id;
      const input = { id, status: slotStatus.CHECKED };
      const checked = await updateBookingRequest({ input });
      hideLoader();
      if (checked.id) setBrand();
    } catch (error) {
      console.log('LOG::  ~ error', error);
    }
  };

  const handleDeleteSlot = (id, visit) => {
    const selectedSlot = visit?.slots?.items?.find((s) => s.id === id);
    const hasConfirmedReq = selectedSlot.occupants?.items?.find(
      (i) => i.status === 'confirmed'
    );
    setDeleteDialog({
      isOpen: true,
      id,
      isApp: false,
      customText: '',
      customSubText: !hasConfirmedReq
        ? ''
        : i18n.dialogs.delete.subtextSingleWithVisit,
      app: {},
      slot: visit,
      multipleSlotsIds: [],
    });
  };

  const handleDeleteMultipleSlot = (ids) => {
    const visit = brand.visits.items.find((v) =>
      v.slots.items.find((s) => s.id === ids[0])
    );
    const selectedSlots = visit.slots?.items?.filter((s) => ids.includes(s.id));
    const hasConfirmedReq = selectedSlots.find((s) =>
      s?.occupants?.items?.find((o) => o.status === 'confirmed')
    );
    setDeleteDialog({
      isOpen: true,
      id: '',
      isApp: false,
      customText: i18n.dialogs.delete.titleMultiple,
      customSubText: !hasConfirmedReq
        ? i18n.dialogs.delete.subTextMultiple
        : i18n.dialogs.delete.subtextMultipleWithVisit,
      app: {},
      slot: {},
      multipleSlotsIds: ids,
    });
  };

  const handleAddMorningSlot = (appointment) => {
    setAddDialog({
      isOpen: true,
      appDate: appointment.date,
      dayTime: 'am',
      appointmentId: appointment.id,
    });
  };

  const handleSettings = () => {
    setSettingsDialog({
      isOpen: true,
      email: brandSettings?.email || '',
      prenotazione_appuntamenti: brandSettings?.prenotazione_appuntamenti,
    });
  };

  const handleAddAfternoonSlot = (appointment) => {
    setAddDialog({
      isOpen: true,
      appDate: appointment.date,
      dayTime: 'pm',
      appointmentId: appointment.id,
    });
  };

  const removeMultiplSlots = async () => {
    const visit = brand.visits.items.find((v) =>
      v.slots.items.find((s) => s.id === deleteDialog?.multipleSlotsIds[0])
    );
    await Promise.all(
      deleteDialog?.multipleSlotsIds.map((slotId) =>
        removeSlot({ visit, slotId })
      )
    );
  };

  const removeSlot = async ({ visit = {}, slotId = '' }) => {
    const appointment = visit?.id ? visit : deleteDialog.slot;
    showLoader();
    try {
      const id = slotId || deleteDialog.id;
      const input = { id };
      const removed = await deleteSlot({ input });
      if (
        removed.id &&
        appointment.slots &&
        appointment.slots.items &&
        appointment.slots.items.length
      ) {
        const promisesOccupants = [];
        const emailPromises = [];
        const occupantsInSlots = [];
        appointment.slots.items.map((s, idx) => {
          if (s.id === removed.id) {
            const slotOccupants = s.occupants?.items || [];
            if (slotOccupants?.length) {
              occupantsInSlots.push(idx);
              slotOccupants.map((occ) => {
                const input = { id: occ.id };
                promisesOccupants.push(deleteBookingRequest({ input }));
              });
            }
          }
        });
        if (promisesOccupants.length) {
          await Promise.all(promisesOccupants);
          occupantsInSlots.map((index) => {
            const occupants = appointment.slots.items[index].occupants;
            if (occupants.items && occupants.items.length) {
              occupants.items.map((o) => {
                const reasonParsed = o.reason;

                const referent = o.referent;

                const start = new Date(appointment.slots.items[index].start);
                const end = new Date(appointment.slots.items[index].end);

                const data = {
                  userName: o.givenName,
                  userLastname: o.familyName,
                  userEmail: o.email,
                  userSalesforceId: o.userSalesforceId,
                  userCompany: o.company || 'N.A.',
                  brandName: o.brandName,
                  brandSalesforceId: o.brandSalesforceId,
                  brandEmail: o.brandEmail,
                  date: start.toISOString(),
                  start: start.toISOString(),
                  end: end.toISOString(),
                  pavillion: o.pavillion || 'N.A.',
                  stand: o.stand || 'N.A.',
                  ...(referent && {
                    referentName: `${referent.familyName} ${referent.givenName}`,
                  }),
                  note: reasonParsed,
                };
                emailPromises.push(
                  emailHelper.sendAgendaEmail({
                    type: 'Delete',
                    data,
                  })
                );
              });
            }
          });
          if (emailPromises.length) {
            Promise.all(emailPromises);
          }
        }

        setDeleteDialog({
          isOpen: false,
          id: '',
          isApp: false,
          customText: '',
          customSubText: '',
          app: {},
          slot: {},
          multipleSlotsIds: [],
        });
        hideLoader();
        // TODO: HANDLE DELETE EMAIL NOTIFICATION
        if (removed.id) setBrand();
      }
    } catch (error) {
      console.log('LOG::  ~ error', error);
    }
  };

  const removeAppointment = async () => {
    const appointment = deleteDialog.app;
    showLoader();
    try {
      const id = deleteDialog.id;
      const input = { id };
      const removed = await deleteBookingRequest({ input });
      const reasonParsed = appointment.reason;

      const referent = appointment.referent;

      const start = new Date(appointment.start);
      const end = new Date(appointment.end);

      const data = {
        userName: appointment.givenName,
        userLastname: appointment.familyName,
        userEmail: appointment.email,
        userSalesforceId: appointment.userSalesforceId,
        userCompany: appointment.company || 'N.A.',
        brandName: appointment.brandName,
        brandSalesforceId: appointment.brandSalesforceId,
        brandEmail: appointment.brandEmail,
        date: start.toISOString(),
        start: start.toISOString(),
        end: end.toISOString(),
        pavillion: appointment.pavillion || 'N.A.',
        stand: appointment.stand || 'N.A.',
        ...(referent && {
          referentName: `${referent.familyName} ${referent.givenName}`,
        }),
        note: reasonParsed,
      };
      await emailHelper.sendAgendaEmail({
        type: 'Delete',
        data,
      });
      setDeleteDialog({
        isOpen: false,
        id: '',
        isApp: false,
        customText: '',
        customSubText: '',
        app: {},
        slot: {},
        multipleSlotsIds: [],
      });
      hideLoader();
      // TODO: HANDLE DELETE EMAIL NOTIFICATION
      if (removed.id) setBrand();
    } catch (error) {
      console.log('LOG::  ~ error', error);
    }
  };

  const handleAddSlot = async (data) => {
    showLoader();
    try {
      const input = data;
      const add = await createSlot({ input });
      setAddDialog({
        isOpen: false,
        appDate: '',
        dayTime: '',
        appointmentId: '',
      });
      hideLoader();
      if (add.id) setBrand();
    } catch (error) {
      console.log('LOG::  ~ error', error);
    }
  };

  const handleEditSettings = async (data) => {
    showLoader();
    try {
      const input = {
        ...data,
        prenotazione_appuntamenti: data.prenotazione_appuntamenti === '0',
      };
      const errRes = await updateExhibitorSettings({
        network,
        id: brandSalesforceId,
        data: {
          ...input,
        },
      });
      setSettingsDialog({
        isOpen: false,
        ...data,
      });
      hideLoader();
      if (!errRes) {
        setBrandSettings(data);
      }
    } catch (error) {
      console.log('LOG::  ~ error', error);
    }
  };

  // Renders
  const renderDeleteDialog = () => {
    if (!deleteDialog.isOpen) return null;
    return (
      <DeleteDialog
        onClose={() => {
          setDeleteDialog({
            isOpen: false,
            id: '',
            isApp: false,
            app: {},
            customText: '',
            customSubText: '',
            slot: {},
            multipleSlotsIds: [],
          });
        }}
        onAgree={() =>
          deleteDialog.isApp
            ? removeAppointment()
            : deleteDialog?.multipleSlotsIds?.length
            ? removeMultiplSlots()
            : removeSlot({ visit: {}, slotId: '' })
        }
        i18n={i18n.dialogs.delete}
        isSmDown={isSmDown}
        customText={deleteDialog.customText || ''}
        customSubText={deleteDialog.customSubText || ''}
      />
    );
  };
  const renderViewDialog = () => {
    if (!viewDialog.isOpen) return null;
    return (
      <ViewDialog
        onClose={() => {
          setViewDialog({ isOpen: false, slot: {} });
        }}
        i18n={i18n.dialogs.view}
        isSmDown={isSmDown}
        locale={locale}
        slot={viewDialog.slot}
      />
    );
  };
  const renderAddDialog = () => {
    if (!addDialog.isOpen) return null;
    return (
      <AddDialog
        onClose={() => {
          setAddDialog({
            isOpen: false,
            appDate: '',
            dayTime: '',
            appointmentId: '',
          });
        }}
        onAgree={handleAddSlot}
        i18n={i18n.dialogs.add}
        isSmDown={isSmDown}
        dayTime={addDialog.dayTime}
        appDate={addDialog.appDate}
        appointmentId={addDialog.appointmentId}
        locale={locale}
      />
    );
  };
  const renderSettingsDialog = () => {
    if (!settingsDialog.isOpen) return null;
    return (
      <SettingsDialog
        onClose={() => {
          setSettingsDialog({
            isOpen: false,
            email: '',
            prenotazione_appuntamenti: '0',
          });
        }}
        onAgree={handleEditSettings}
        i18n={i18n.dialogs.settings}
        isSmDown={isSmDown}
        isOpen={settingsDialog.isOpen}
        email={settingsDialog.email}
        prenotazione_appuntamenti={settingsDialog.prenotazione_appuntamenti}
      />
    );
  };
  /*
  const calcAlreadyOpen = (a, idx) => {
    const today = new Date();
    const formatToday = format(today, 'yyyy-MM-dd');
    const event = SaloneStartDate;
    const formatAppDate = format(new Date(a.date), 'yyyy-MM-dd');
    const isEventStart = isAfter(new Date(formatToday), new Date(event));
    return isEventStart ? formatToday === formatAppDate : idx === 0;
  };
*/
  const CustomTabs = () => (
    <CustomStack>
      <StyledTab
        onClick={() => {
          setContextSelected(slotContext.SLOT);
          setSearchText('');
        }}
        isSelected={contextSelected === slotContext.SLOT}
        style={{ fontWeight: contextSelected === slotContext.SLOT ? 800 : 400 }}
      >
        {i18n.buttons.slot}
      </StyledTab>
      <StyledTab
        onClick={() => setContextSelected(slotContext.REQUEST)}
        isSelected={contextSelected === slotContext.REQUEST}
        style={{
          fontWeight: contextSelected === slotContext.REQUEST ? 800 : 400,
        }}
      >
        {i18n.buttons.request}
      </StyledTab>
    </CustomStack>
  );

  return (
    <Grid container>
      <div style={{ width: '100%' }}>
        {renderDeleteDialog()}
        {renderViewDialog()}
        {renderAddDialog()}
        {renderSettingsDialog()}
        <PageHeader i18n={i18n} isBrandPage={false} />
        <div
          style={{
            width: isSmDown ? '90%' : '80%',
            margin: '0 auto',
            maxWidth: 830,
          }}
        >
          {brand ? (
            <SdmTypography
              variant={isSmDown ? 'h4Regular' : 'h2Regular'}
              style={{
                width: '100%',
                color: theme.colors.primary.black,
              }}
              textAlign="center"
            >
              {brand?.name ? (
                <>
                  {i18n.textWithBrand}{' '}
                  <span style={{ fontWeight: 'bold' }}>{brand?.name}</span>
                </>
              ) : (
                i18n.text
              )}
            </SdmTypography>
          ) : null}

          {brand && !brand?.visits?.items.length && !isLoaderActive ? (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
              }}
            >
              <SdmSpacer height={60} />
              <SdmTypography
                variant={isSmDown ? 'h4' : 'h1'}
                style={{
                  width: '100%',
                  color: theme.colors.primary.black,
                }}
              >
                {i18n.noVisit}
              </SdmTypography>
              <SdmTypography
                variant={isSmDown ? 'h5' : 'h2'}
                style={{
                  width: '100%',
                  color: theme.colors.primary.black,
                }}
              >
                {i18n.addMsg}
              </SdmTypography>
              <SdmSpacer height={20} />
            </div>
          ) : null}

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
            }}
          >
            <SdmSpacer height={20} />
            <SdmTextButton
              height={46}
              textVariant={isSmDown ? 'h4Regular' : 'h2Regular'}
              text={i18n.btn}
              variant="redFill"
              onClick={() => createVisit()}
            />
          </div>
          {brand && brandSettings && hasValidSettings(brandSettings) ? (
            <SettingsGreyBox
              {...{
                brandSettings,
                brandSalesforceId,
              }}
              showModal={handleSettings}
            />
          ) : (
            <></>
          )}
        </div>
        <SdmSpacer
          height={
            brand && brandSettings && hasValidSettings(brandSettings) ? 50 : 90
          }
        />
        {!isLoaderActive && brand?.visits?.items.length ? (
          <Grid item xs={12}>
            <CustomTabs />
            <div style={{ backgroundColor: '#F3F3F3' }}>
              <SdmSpacer height={isSmDown ? 20 : 60} />
              <CardContainer>
                <Grid container>
                  <Grid item sm={8} xs={false}></Grid>
                  <Grid item sm={4} xs={12}>
                    {contextSelected === slotContext.REQUEST && (
                      <CustomSdmTextField
                        value={searchText}
                        onChange={(e) => setSearchText(e.target.value)}
                        placeholder={i18n.searchTextfield}
                        inputPropsStyle={{ marginTop: 0 }}
                        ismobile={isXsDown ? 1 : 0}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <SearchIcon />
                            </InputAdornment>
                          ),
                        }}
                      />
                    )}
                  </Grid>
                </Grid>

                {contextSelected === slotContext.SLOT ? (
                  <>
                    {brand?.visits?.items.length
                      ? brand?.visits?.items
                          .filter((v) => !!v?.slots?.items?.length)
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .map((a, idx) => (
                            <AppointmentCard
                              key={idx}
                              appointment={a}
                              locale={locale}
                              i18n={i18n.cards}
                              isSmDown={isSmDown}
                              isXsDown={isXsDown}
                              alreadyOpen={false} //{calcAlreadyOpen(a, idx)}
                              onViewSlot={handleViewSlot}
                              onDeleteSlot={handleDeleteSlot}
                              onAddMorning={handleAddMorningSlot}
                              onAddAfternoon={handleAddAfternoonSlot}
                              onMultipleDeleteSlot={handleDeleteMultipleSlot}
                            />
                          ))
                      : null}
                  </>
                ) : (
                  <>
                    {brand?.visits?.items.length
                      ? brand?.visits?.items
                          .sort((a, b) => new Date(a.date) - new Date(b.date))
                          .filter(
                            (a) =>
                              !!a.slots?.items.filter(
                                (s) => s.occupants?.items?.length
                              ).length
                          )
                          .map((a, idx) => (
                            <RequestCard
                              key={idx}
                              appointment={a}
                              locale={locale}
                              i18n={i18n.bookingCards}
                              isSmDown={isSmDown}
                              isXsDown={isXsDown}
                              alreadyOpen={false}
                              onViewRequest={handleViewRequest}
                              onDeleteRequest={handleDeleteRequest}
                              onCheckRequest={handleCheckRequest}
                              searchText={searchText}
                            />
                          ))
                      : null}
                  </>
                )}
              </CardContainer>
            </div>
          </Grid>
        ) : null}
      </div>
    </Grid>
  );
};
export default Agenda;
